import { Plus, Repeat, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { RoutineActivity, RoutineDTO, TimeWindow } from '@tempo/core';
import { api } from '../api';
import { Button, ColorPicker, Empty, Field, Loading, Modal, NumberInput, Segmented, Switch } from '../components/ui';
import { DAY_LETTERS, dateOf, daysLabel, dt, minutes, time, todayISO } from '../lib/format';
import { useAction, usePlaces, usePlan, useRoutines } from '../lib/hooks';
import { setParams, type Route } from '../lib/router';

const PRIORITY = ['', 'Nice to have', 'Low', 'Normal', 'High', 'Must happen'];

export function RoutinesPage({ route }: { route: Route }) {
  const routines = useRoutines();
  const today = todayISO();
  const plan = usePlan(today, 1);
  const [editing, setEditing] = useState<RoutineDTO | 'new' | null>(null);

  const editId = route.params.get('edit');
  useEffect(() => {
    if (editId && routines.data) {
      const r = routines.data.find((x) => x.id === editId);
      if (r) setEditing(r);
      setParams({ edit: null });
    }
  }, [editId, routines.data]);

  const placedToday = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const i of plan.data?.items ?? []) {
      if (i.kind === 'routine' && i.refId && dateOf(i.start) === today) m.set(i.refId, [...(m.get(i.refId) ?? []), `${time(i.start)}–${time(i.end)}`]);
    }
    return m;
  }, [plan.data, today]);
  const unplacedToday = new Set((plan.data?.issues ?? []).filter((i) => i.type === 'routine_unplaced' && i.date === today).flatMap((i) => i.refIds));
  const dow = dt(Date.now()).weekday;

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="grow">Routines</h1>
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus size={16} /> Add routine
        </Button>
      </div>
      <p className="subtle small" style={{ marginBottom: 14 }}>
        The things you want every day. Tempo finds each one a slot around your shifts, lectures and plans – and moves it automatically whenever your day changes.
      </p>
      {routines.isLoading ? (
        <Loading />
      ) : !routines.data?.length ? (
        <div className="card">
          <Empty icon={<Repeat size={28} />} title="No routines yet">
            Add gym, meals, a daily study hour, reading…
          </Empty>
        </div>
      ) : (
        <div className="stack">
          {routines.data.map((r) => (
            <RoutineCard
              key={r.id}
              r={r}
              today={!r.days.includes(dow) ? 'Not today' : placedToday.get(r.id)?.join(', ') ?? (unplacedToday.has(r.id) ? 'No room today' : r.enabled ? '–' : 'Off')}
              warn={unplacedToday.has(r.id)}
              onEdit={() => setEditing(r)}
            />
          ))}
        </div>
      )}
      {editing && <RoutineForm existing={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function windowsLabel(w: TimeWindow[]) {
  return w.length ? w.map((x) => `${x.start}–${x.end}`).join(' or ') : 'any time';
}

function RoutineCard({ r, today, warn, onEdit }: { r: RoutineDTO; today: string; warn: boolean; onEdit: () => void }) {
  const toggle = useAction((enabled: boolean) => api.patch(`/routines/${r.id}`, { enabled }));
  const color = r.color ?? '#10b981';
  return (
    <div className="card list-card" style={{ opacity: r.enabled ? 1 : 0.6, cursor: 'pointer' }} onClick={onEdit}>
      <div className="accent-bar" style={{ background: color }} />
      <div className="grow">
        <div className="title">{r.name}</div>
        <div className="meta">
          <span>
            {minutes(r.durationMin)}
            {r.minDurationMin ? ` (min ${minutes(r.minDurationMin)})` : ''}
          </span>
          <span>· {daysLabel(r.days)}</span>
          <span>· {windowsLabel(r.windows)}</span>
          {r.preferredTime && <span>· aims for {r.preferredTime}</span>}
          {r.splittable && <span>· can split</span>}
          <span>· {PRIORITY[r.priority]}</span>
        </div>
        <div className="small" style={{ marginTop: 6, color: warn ? 'var(--warn)' : 'var(--text-2)' }}>
          Today: {today}
        </div>
      </div>
      <div className="actions" onClick={(e) => e.stopPropagation()}>
        <button className={`switch ${r.enabled ? 'on' : ''}`} onClick={() => toggle.mutate(!r.enabled)} aria-label="Enabled" />
      </div>
    </div>
  );
}

function RoutineForm({ existing, onClose }: { existing?: RoutineDTO; onClose: () => void }) {
  const places = usePlaces().data ?? [];
  const [name, setName] = useState(existing?.name ?? '');
  const [color, setColor] = useState(existing?.color ?? '#10b981');
  const [duration, setDuration] = useState<number | null>(existing?.durationMin ?? 60);
  const [canShrink, setCanShrink] = useState(existing?.minDurationMin != null);
  const [minDuration, setMinDuration] = useState<number | null>(existing?.minDurationMin ?? 30);
  const [days, setDays] = useState<number[]>(existing?.days ?? [1, 2, 3, 4, 5, 6, 7]);
  const [windows, setWindows] = useState<TimeWindow[]>(existing?.windows ?? []);
  const [preferred, setPreferred] = useState(existing?.preferredTime ?? '');
  const [priority, setPriority] = useState(existing?.priority ?? 3);
  const [splittable, setSplittable] = useState(existing?.splittable ?? false);
  const [minChunk, setMinChunk] = useState<number | null>(existing?.minChunkMin ?? 30);
  const [placeId, setPlaceId] = useState(existing?.placeId ?? '');
  const [bufferAfter, setBufferAfter] = useState<number | null>(existing?.bufferAfterMin ?? 0);
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [activity, setActivity] = useState<RoutineActivity | ''>(existing?.activity ?? '');

  const body = () => ({
    name: name.trim(),
    color,
    durationMin: duration ?? 30,
    minDurationMin: canShrink ? Math.min(minDuration ?? 15, duration ?? 30) : null,
    days,
    windows,
    preferredTime: preferred || null,
    priority,
    splittable,
    minChunkMin: splittable ? (minChunk ?? 15) : null,
    placeId: placeId || null,
    bufferAfterMin: bufferAfter ?? 0,
    notes: notes || null,
    activity: activity || null,
  });
  const save = useAction(() => (existing ? api.patch(`/routines/${existing.id}`, body()) : api.post('/routines', body())), existing ? 'Saved – re-planned' : 'Added – slotted into your days');
  const remove = useAction(() => api.del(`/routines/${existing!.id}`), 'Routine deleted');

  const setWindow = (i: number, patch: Partial<TimeWindow>) => setWindows(windows.map((w, j) => (j === i ? { ...w, ...patch } : w)));

  return (
    <Modal
      title={existing ? 'Edit routine' : 'New routine'}
      onClose={onClose}
      footer={
        <>
          {existing && (
            <Button className="left" variant="danger" onClick={() => confirm(`Delete "${existing.name}"?`) && remove.mutateAsync().then(onClose).catch(() => undefined)}>
              <Trash2 size={16} /> Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim() || !duration || !days.length} loading={save.isPending} onClick={() => save.mutateAsync().then(onClose).catch(() => undefined)}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <input className="input" autoFocus={!existing} value={name} onChange={(e) => setName(e.target.value)} placeholder="Gym, lunch, reading, Duolingo…" />
        </Field>
        <div className="grid-2">
          <Field label="How long (minutes)">
            <NumberInput value={duration} onChange={setDuration} min={5} step={5} />
          </Field>
          <Field label="Aim for (optional)" hint="It gravitates towards this time">
            <input className="input" type="time" value={preferred} onChange={(e) => setPreferred(e.target.value)} />
          </Field>
        </div>
        <Field label="Which days">
          <div className="chips">
            {DAY_LETTERS.map((l, i) => (
              <button key={i} className={`chip ${days.includes(i + 1) ? 'on' : ''}`} style={{ width: 38, padding: '5px 0' }} onClick={() => setDays(days.includes(i + 1) ? days.filter((d) => d !== i + 1) : [...days, i + 1].sort())}>
                {l}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Allowed times" hint={windows.length ? 'It must fit inside one of these windows.' : 'Any time between your wake-up and bedtime.'}>
          <div className="stack tight">
            {windows.map((w, i) => (
              <div key={i} className="row">
                <input className="input sm" type="time" value={w.start} onChange={(e) => setWindow(i, { start: e.target.value })} />
                <span className="faint">to</span>
                <input className="input sm" type="time" value={w.end} onChange={(e) => setWindow(i, { end: e.target.value })} />
                <Button icon size="sm" variant="ghost" onClick={() => setWindows(windows.filter((_, j) => j !== i))} aria-label="Remove window">
                  <X size={15} />
                </Button>
              </div>
            ))}
            <Button size="sm" style={{ alignSelf: 'flex-start' }} onClick={() => setWindows([...windows, { start: '07:00', end: '10:00' }])}>
              <Plus size={14} /> Add a window
            </Button>
          </div>
        </Field>
        <Field label="Priority" hint="When the day is tight, higher priority routines get their slot first.">
          <Segmented value={priority} onChange={setPriority} options={[1, 2, 3, 4, 5].map((p) => ({ value: p, label: PRIORITY[p] }))} />
        </Field>
        <Switch on={canShrink} onChange={setCanShrink} label="Can be shortened on busy days" />
        {canShrink && (
          <Field label="Shortest acceptable (minutes)">
            <NumberInput value={minDuration} onChange={setMinDuration} min={5} step={5} />
          </Field>
        )}
        <Switch on={splittable} onChange={setSplittable} label="Can be split into chunks" hint="e.g. 2h study as 4 × 30 min" />
        {splittable && (
          <Field label="Smallest chunk (minutes)">
            <NumberInput value={minChunk} onChange={setMinChunk} min={5} step={5} />
          </Field>
        )}
        <div className="grid-2">
          <Field label="Where" hint="Adds travel there and back">
            <select className="input" value={placeId} onChange={(e) => setPlaceId(e.target.value)}>
              <option value="">Anywhere</option>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Breather after (min)">
            <NumberInput value={bufferAfter} onChange={setBufferAfter} min={0} step={5} />
          </Field>
        </div>
        <Field label="In Assistant mode, treat this as" hint="Auto guesses from the name (e.g. Dinner → cooking timers, Gym → workout timer).">
          <select className="input" value={activity} onChange={(e) => setActivity(e.target.value as RoutineActivity | '')}>
            <option value="">Auto</option>
            <option value="cooking">Cooking – timers &amp; recipe steps</option>
            <option value="gym">Workout – session &amp; rest timers</option>
            <option value="study">Study – focus timer &amp; steps</option>
            <option value="rest">Rest / wind down</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Colour">
          <ColorPicker value={color} onChange={setColor} />
        </Field>
        <Field label="Notes" hint="For cooking, write the recipe here – one step per line – and the Assistant steps through it.">
          <textarea className="input" style={{ minHeight: 60 }} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
